import type { Course, Lesson } from '../content/types';
import type { PhraseStatus } from '../components/Status';
import type { Persisted } from '../state/model';
import { pathOf } from './plan';
import { cardId, cardStatus, dueDate, type CardStatus } from './scheduler';

const RANK: Record<CardStatus, number> = { new: 0, learning: 1, known: 2, mastered: 3 };

/** A phrase is only as far along as its weakest card: mastered means every card is mastered. */
export function phraseStatus(state: Persisted, itemId: string, role: 'say' | 'hear'): PhraseStatus {
  if (state.profile.flags[itemId]) return 'flagged';
  const kinds = role === 'say' ? (['say', 'hear'] as const) : (['hear'] as const);
  const cards = kinds.map((k) => state.cards[cardId(itemId, k)]).filter(Boolean);
  if (cards.length === 0) return 'unmet';
  const statuses = kinds.map((k) => {
    const c = state.cards[cardId(itemId, k)];
    return c ? cardStatus(c) : 'new';
  });
  // A phrase that has been met is being learned until all its cards are known, even before its
  // first review or while its second card hasn't come up yet.
  return statuses.map((s) => (s === 'new' ? 'learning' : s)).reduce((a, b) => (RANK[a] <= RANK[b] ? a : b));
}

export type LessonStatus = 'done' | 'current' | 'upcoming';

export function lessonStatus(state: Persisted, lesson: Lesson, course: Course): LessonStatus {
  if (state.profile.lessonsDone.includes(lesson.id)) return 'done';
  // A lesson whose phrases are all flagged has nothing to practise, so it is never "up next".
  const open = (l: Lesson) => !state.profile.lessonsDone.includes(l.id) && l.phrases.some((id) => !state.profile.flags[id]);
  const firstOpen = pathOf(state, course).find(open);
  return firstOpen?.id === lesson.id ? 'current' : 'upcoming';
}

/**
 * How solid a finished lesson's can-do is: "settling" right after the lesson, "solid" once every
 * phrase in it is known or mastered from spaced reviews. Progress means retention, not exposure.
 */
export function candoState(state: Persisted, lesson: Lesson, course: Course): 'settling' | 'solid' | null {
  if (!state.profile.lessonsDone.includes(lesson.id)) return null;
  const solid = lesson.phrases.every((id) => {
    const item = course.items.find((i) => i.id === id);
    const s = item ? phraseStatus(state, id, item.role) : 'flagged';
    return s === 'known' || s === 'mastered' || s === 'flagged';
  });
  return solid ? 'solid' : 'settling';
}

/** When a settling can-do is next checked: the soonest review among its phrases not yet solid. */
export function nextCheck(state: Persisted, lesson: Lesson): Date | null {
  let soonest: Date | null = null;
  for (const id of lesson.phrases) {
    for (const kind of ['say', 'hear'] as const) {
      const card = state.cards[cardId(id, kind)];
      if (!card || ['known', 'mastered'].includes(cardStatus(card))) continue;
      const due = dueDate(card);
      if (!soonest || due < soonest) soonest = due;
    }
  }
  return soonest;
}

/** The newest finished lesson whose real-world mission hasn't been asked about, from an earlier day. */
export function missionToAsk(state: Persisted, today: string): string | null {
  const missions = state.profile.missions ?? {};
  for (const id of [...state.profile.lessonsDone].reverse()) {
    const mission = missions[id];
    if (mission && mission.tried === undefined && mission.day < today) return id;
  }
  return null;
}

export interface Tally {
  unmet: number;
  learning: number;
  known: number;
  mastered: number;
  flagged: number;
}

export function tally(state: Persisted, course: Course, itemIds?: string[]): Tally {
  const t: Tally = { unmet: 0, learning: 0, known: 0, mastered: 0, flagged: 0 };
  for (const item of course.items) {
    if (itemIds && !itemIds.includes(item.id)) continue;
    const s = phraseStatus(state, item.id, item.role);
    if (s === 'unmet') t.unmet++;
    else if (s === 'new' || s === 'learning') t.learning++;
    else if (s === 'known') t.known++;
    else if (s === 'mastered') t.mastered++;
    else t.flagged++;
  }
  return t;
}
